import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Grid, Row, Col } from 'rsuite';
import { selectPaymentCheckFailed } from '../../../../store/slices/payment/paymentSelectors';
import { setPaymentCheckFailed } from '../../../../store/slices/payment/paymentSlice';
import PaymentCheckFailed from './PaymentCheckFailed';
import usePlaceNavigate from '../../../../hooks/usePlaceNavigate';
import { useLocation } from 'react-router-dom';
import { FLOWS_ENUM } from 'src/common/enums';
import { buildPaymentApi } from 'src/api';
import { selectCurrentIDToken } from 'src/store/slices/auth/authSelectors';
import { selectPciPaymentApiEnabled } from 'src/store/slices/config/configSelectors';
import { useAppDispatch } from 'src/store/store';
import { useCloseWindow } from 'src/hooks/useCloseWindow';

interface SuccessProps {
  successComponent?: () => React.ReactElement;
  showReturnToDashboardButton?: boolean;
  flow: FLOWS_ENUM;
}

const Success: React.FC<SuccessProps> = ({
  successComponent = () => null,
  showReturnToDashboardButton = true,
  flow,
}) => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const paymentCheckFailed = useSelector(selectPaymentCheckFailed);
  const authToken = useSelector(selectCurrentIDToken);
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const api = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  const { isAbleToCloseWindow, closeWindow } = useCloseWindow();

  const executeCheckPayment = useCallback(
    async (paymentId: string) => {
      if (paymentId) {
        try {
          if (flow === FLOWS_ENUM.PAY_TOT) {
            await api.checkTaxPayment(authToken, paymentId);
          } else if (
            flow === FLOWS_ENUM.APPLY_LICENSE ||
            flow === FLOWS_ENUM.RENEW_LICENSE
          ) {
            await api.checkLicensePayment(authToken, paymentId);
          }
        } catch (error) {
          console.error('Check was not completed successfully', error);
          dispatch(setPaymentCheckFailed(true));
        }
      }
    },
    [authToken, flow, api, dispatch]
  );

  useEffect(() => {
    const paymentId = params.get('paymentId');

    if (paymentId) {
      executeCheckPayment(paymentId);
    }
  }, [params, executeCheckPayment]);

  return (
    <Grid>
      <Row className="mb-16 mt-16">
        <Col xs={24}>
          {paymentCheckFailed && <PaymentCheckFailed />}

          {!paymentCheckFailed && successComponent()}
        </Col>
      </Row>
      {showReturnToDashboardButton && !isAbleToCloseWindow && (
        <Row>
          <Col xs={24}>
            <Button
              className="btn-primary"
              onClick={() => placeNavigate('/dashboard')}
            >
              Return to Dashboard
            </Button>
          </Col>
        </Row>
      )}
      {isAbleToCloseWindow && (
        <Row>
          <Col xs={24}>
            <Button
              className="btn-primary"
              onClick={closeWindow}
            >
              Close window
            </Button>
          </Col>
        </Row>
      )}
    </Grid>
  );
};

export default Success;
